import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, BUSINESS } from "../lib/content";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#09090b]/90 backdrop-blur-xl border-b border-[#27272a]"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between h-20 md:h-24">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="nav-logo-link"
          onClick={() => setOpen(false)}
        >
          <div className="relative">
            <div className="w-11 h-11 border-2 border-[#f59e0b] flex items-center justify-center bg-[#09090b]">
              <span className="font-display font-black text-[#f59e0b] text-xl leading-none">
                NW
              </span>
            </div>
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-display font-black text-[#fafaf9] text-lg tracking-wide uppercase">
              North West
            </div>
            <div className="text-[10px] tracking-[0.3em] text-[#a1a1aa] uppercase">
              Asphalt · Since {BUSINESS.established}
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={link.testId}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm uppercase tracking-[0.18em] font-semibold transition-colors ${
                  isActive
                    ? "text-[#f59e0b]"
                    : "text-[#fafaf9] hover:text-[#f59e0b]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-[#f59e0b]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            className="inline-flex items-center gap-2 bg-[#f59e0b] hover:bg-[#d97706] text-[#09090b] font-bold px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] transition-colors"
            data-testid="nav-call-cta"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={3} />
            {BUSINESS.phone}
          </a>
          <a
            href={`tel:${BUSINESS.phone2Raw}`}
            className="hidden lg:inline-flex items-center gap-2 border border-[#27272a] hover:border-[#f59e0b] text-[#fafaf9] font-bold px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] transition-colors"
            data-testid="nav-call-cta-2"
          >
            <Phone className="w-3.5 h-3.5 text-[#f59e0b]" strokeWidth={3} />
            {BUSINESS.phone2}
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          className="lg:hidden text-[#fafaf9] p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {/* Mobile Drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          open ? "max-h-[600px]" : "max-h-0"
        } bg-[#09090b] border-t border-[#27272a]`}
        data-testid="nav-mobile-drawer"
      >
        <div className="px-5 py-6 flex flex-col gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={() => setOpen(false)}
              data-testid={`${link.testId}-mobile`}
              className={({ isActive }) =>
                `py-4 px-2 text-lg uppercase tracking-[0.15em] font-semibold border-b border-[#27272a] ${
                  isActive ? "text-[#f59e0b]" : "text-[#fafaf9]"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            onClick={() => setOpen(false)}
            className="mt-5 inline-flex items-center justify-center gap-2 bg-[#f59e0b] text-[#09090b] font-bold px-5 py-4 text-sm uppercase tracking-[0.2em]"
            data-testid="nav-call-cta-mobile"
          >
            <Phone className="w-4 h-4" strokeWidth={3} />
            Call {BUSINESS.phone}
          </a>
          <a
            href={`tel:${BUSINESS.phone2Raw}`}
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex items-center justify-center gap-2 border border-[#27272a] text-[#fafaf9] font-bold px-5 py-4 text-sm uppercase tracking-[0.2em] hover:border-[#f59e0b] transition-colors"
            data-testid="nav-call-cta-mobile-2"
          >
            <Phone className="w-4 h-4 text-[#f59e0b]" strokeWidth={3} />
            Call {BUSINESS.phone2}
          </a>
        </div>
      </div>
    </header>
  );
};
